import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold, faItalic, faUnderline, faStrikethrough,
  faAlignLeft, faAlignCenter, faAlignRight, faAlignJustify,
  faListUl, faListOl, faUndo, faRedo
} from '@fortawesome/free-solid-svg-icons';
import './StyledText.css';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const toggleAction = (e, action) => {
    e.preventDefault();
    action();
  };

  return (
    <div className="tiptap-menubar">
      <div className="tiptap-toolbar-group">
        <button className={`tiptap-btn ${editor.isActive('bold') ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().toggleBold().run())} title="عريض">
          <FontAwesomeIcon icon={faBold} />
        </button>
        <button className={`tiptap-btn ${editor.isActive('italic') ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().toggleItalic().run())} title="مائل">
          <FontAwesomeIcon icon={faItalic} />
        </button>
        <button className={`tiptap-btn ${editor.isActive('underline') ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().toggleUnderline().run())} title="تسطير">
          <FontAwesomeIcon icon={faUnderline} />
        </button>
        <button className={`tiptap-btn ${editor.isActive('strike') ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().toggleStrike().run())} title="يتوسطه خط">
          <FontAwesomeIcon icon={faStrikethrough} />
        </button>
      </div>

      <div className="tiptap-divider" />

      <div className="tiptap-toolbar-group">
        <button className={`tiptap-btn ${editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().setTextAlign('right').run())} title="محاذاة لليمين">
          <FontAwesomeIcon icon={faAlignRight} />
        </button>
        <button className={`tiptap-btn ${editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().setTextAlign('center').run())} title="توسيط">
          <FontAwesomeIcon icon={faAlignCenter} />
        </button>
        <button className={`tiptap-btn ${editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().setTextAlign('left').run())} title="محاذاة لليسار">
          <FontAwesomeIcon icon={faAlignLeft} />
        </button>
        <button className={`tiptap-btn ${editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().setTextAlign('justify').run())} title="ضبط">
          <FontAwesomeIcon icon={faAlignJustify} />
        </button>
      </div>

      <div className="tiptap-divider" />

      <div className="tiptap-toolbar-group">
        <button className={`tiptap-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().toggleBulletList().run())} title="قائمة نقطية">
          <FontAwesomeIcon icon={faListUl} />
        </button>
        <button className={`tiptap-btn ${editor.isActive('orderedList') ? 'is-active' : ''}`} onClick={(e) => toggleAction(e, () => editor.chain().focus().toggleOrderedList().run())} title="قائمة رقمية">
          <FontAwesomeIcon icon={faListOl} />
        </button>
      </div>

      <div className="tiptap-divider" />

      <div className="tiptap-toolbar-group">
        <button className="tiptap-btn" onClick={(e) => toggleAction(e, () => editor.chain().focus().undo().run())} disabled={!editor.can().undo()} title="تراجع">
          <FontAwesomeIcon icon={faUndo} />
        </button>
        <button className="tiptap-btn" onClick={(e) => toggleAction(e, () => editor.chain().focus().redo().run())} disabled={!editor.can().redo()} title="إعادة">
          <FontAwesomeIcon icon={faRedo} />
        </button>
      </div>
    </div>
  );
};

const StyledText = ({ value, onChange, placeholder, height = 400, readOnly = false, className = '' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (onChange) onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
        dir: 'rtl',
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      // Small timeout to prevent state update loops if triggered closely
      setTimeout(() => {
         if (editor.getHTML() !== value) {
            editor.commands.setContent(value || '');
         }
      }, 0);
    }
  }, [value, editor]);

  return (
    <div className={`styled-text-container ${className} ${readOnly ? 'is-readonly' : ''}`}>
      {!readOnly && <MenuBar editor={editor} />}
      <div className="tiptap-content-wrapper" style={{ minHeight: height }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default StyledText;
